import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import {
  MAXIMALNY_POCET_POPISOV_OSI,
  POCET_MESIACOV_V_ROKU,
  cislo,
  cssPremenna,
  formatujMesiacOsi,
  formatujRokOsi,
  formatujSumu,
  pripravVyvojGrafu,
  textDosiahnutiaCiela,
  useTemaGrafu,
  vypocitajRealnuHodnotu,
  vytvorObrazokGrafu,
} from "./investGrafPomocne";

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const InvesticneVyvojGrafKarta =
  React.forwardRef(
    function InvesticneVyvojGrafKarta(
      {
        vysledok,
        vysledkyInvesticii,
        porovnanieInvesticii,
        nastaveniaVariantov,
      },
      ref
    ) {
  const temaGrafu = useTemaGrafu();
  const chartRef = React.useRef(null);

  const farbaVariant1 = cssPremenna(
    "--invest-graf-variant-1",
    "rgba(100, 171, 210, 0.96)"
  );

  const farbaVariant2 = cssPremenna(
    "--invest-graf-variant-2",
    "rgba(156, 139, 96, 0.96)"
  );

  const textFarba = cssPremenna(
    "--text",
    "#f3f3f3"
  );

  const mutedFarba = cssPremenna(
    "--muted",
    "rgba(255,255,255,0.68)"
  );

  const lineFarba = cssPremenna(
    "--line",
    "rgba(255,255,255,0.12)"
  );

  const surfaceFarba = cssPremenna(
    "--surface",
    "#1f1f1f"
  );

  const {
    maPorovnanie,
    maximalnyMesiac,
    maData,
    cieloveVarianty,
    datasets,
  } = pripravVyvojGrafu({
    vysledok,
    vysledkyInvesticii,
    porovnanieInvesticii,
    nastaveniaVariantov,
    farbaVariant1,
    farbaVariant2,
    surfaceFarba,
  });

  const data = {
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          color: textFarba,
          boxWidth: 18,
          boxHeight: 2,
          padding: 10,
          font: {
            size: 10,
            weight: "600",
          },
        },
      },
      tooltip: {
        backgroundColor: surfaceFarba,
        titleColor: textFarba,
        bodyColor: textFarba,
        borderColor: lineFarba,
        borderWidth: 1,
        filter: (ctx) =>
          ctx.dataset?.typ !==
          "cielovaSuma",
        callbacks: {
          title: (polozky) =>
            formatujMesiacOsi(
              polozky?.[0]?.parsed?.x
            ),
          label: (ctx) =>
            `${
              ctx.dataset.plnyNazov ||
              ctx.dataset.label
            }: ${formatujSumu(
              ctx.parsed.y
            )}`,
          afterBody: (polozky) => {
            const hlavnePolozky =
              polozky.filter(
                (polozka) =>
                  polozka.dataset
                    ?.typ === "hlavny"
              );

            return hlavnePolozky.flatMap(
              (hlavnaPolozka) => {
                const dataset =
                  hlavnaPolozka.dataset;

                const mesiac = Math.max(
                  0,
                  Math.round(
                    cislo(
                      hlavnaPolozka
                        .parsed?.x
                    )
                  )
                );

                const bod =
                  dataset.priebeh?.find(
                    (polozka) =>
                      cislo(
                        polozka?.mesiac
                      ) === mesiac
                  );

                if (!bod) {
                  return [];
                }

                const jednorazovyPrivkladVMesiaci =
                  mesiac ===
                  dataset.mesiacPrivkladu
                    ? dataset
                        .jednorazovyPrivklad
                    : 0;

                const pociatocnyJednorazovyVklad =
                  mesiac === 0
                    ? dataset
                        .jednorazovyVklad
                    : 0;

                const aktualnyMesacnyVklad =
                  mesiac > 0
                    ? Math.max(
                        0,
                        cislo(
                          bod.hrubyVklad
                        ) -
                          jednorazovyPrivkladVMesiaci
                      )
                    : 0;

                const doplnkoveUdaje = [];

                if (
                  dataset.inflacia > 0 &&
                  mesiac > 0
                ) {
                  doplnkoveUdaje.push(
                    `reálne ${formatujSumu(
                      vypocitajRealnuHodnotu(
                        hlavnaPolozka
                          .parsed?.y,
                        dataset.inflacia,
                        mesiac
                      )
                    )}`
                  );
                }

                if (aktualnyMesacnyVklad > 0) {
                  doplnkoveUdaje.push(
                    `mesačne ${formatujSumu(
                      aktualnyMesacnyVklad
                    )}`
                  );
                }

                const riadky =
                  doplnkoveUdaje.length > 0
                    ? [
                        `${dataset.kratkyLabel}: ${doplnkoveUdaje.join(
                          " · "
                        )}`,
                      ]
                    : [];

                if (
                  pociatocnyJednorazovyVklad >
                  0
                ) {
                  riadky.push(
                    `${dataset.kratkyLabel}: jednorazový vklad ${formatujSumu(
                      pociatocnyJednorazovyVklad
                    )}`
                  );
                }

                if (
                  jednorazovyPrivkladVMesiaci >
                  0
                ) {
                  riadky.push(
                    `${dataset.kratkyLabel}: prívklad ${formatujSumu(
                      jednorazovyPrivkladVMesiaci
                    )}`
                  );
                }

                if (
                  mesiac ===
                  dataset
                    .mesiacUkonceniaVkladov
                ) {
                  riadky.push(
                    `${dataset.kratkyLabel}: začiatok dodatočného zhodnocovania`
                  );
                }

                return riadky;
              }
            );
          },
        },
      },
    },
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: maximalnyMesiac,
        bounds: "ticks",
        ticks: {
          color: mutedFarba,
          maxRotation: 0,
          autoSkip: true,
          includeBounds: true,
          stepSize:
            POCET_MESIACOV_V_ROKU,
          maxTicksLimit:
            MAXIMALNY_POCET_POPISOV_OSI,
          callback: (value) =>
            formatujRokOsi(value),
          font: {
            size: 10,
          },
        },
        grid: {
          color: lineFarba,
        },
        border: {
          color: lineFarba,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: mutedFarba,
          callback: (value) =>
            formatujSumu(value),
          font: {
            size: 10,
          },
        },
        grid: {
          color: lineFarba,
        },
        border: {
          color: lineFarba,
        },
      },
    },
  };

  React.useImperativeHandle(
    ref,
    () => ({
      ziskajPdfData: () => ({
        obrazok: maData
          ? vytvorObrazokGrafu(
              chartRef.current,
              surfaceFarba
            )
          : null,
        poznamky:
          cieloveVarianty.map(
            (variant) => {
              const textCiela =
                textDosiahnutiaCiela(
                  variant
                );

              return {
                label: variant.label,

                riadky: [
                  textCiela.text
                    ? {
                        text:
                          textCiela.text,
                        zvyraznit:
                          false,
                      }
                    : null,

                  textCiela
                    .zvyraznenie
                    ? {
                        text:
                          textCiela
                            .zvyraznenie,
                        zvyraznit:
                          true,
                      }
                    : null,
                ].filter(Boolean),
              };
            }
          ),
      }),
    }),
    [
      maData,
      surfaceFarba,
      cieloveVarianty,
    ]
  );

  if (!maData) {
    return (
      <div className="invest-graf-empty">
        Zadaj vstupné hodnoty pre
        zobrazenie grafu.
      </div>
    );
  }

  return (
    <>
      <div
        className={`invest-line-wrap ${
          maPorovnanie
            ? "is-compare"
            : ""
        }`}
      >
        <Line
          ref={chartRef}
          key={temaGrafu}
          data={data}
          options={options}
        />
      </div>

      {cieloveVarianty.length > 0 && (
        <div className="invest-graf-realne invest-graf-ciele">
          {cieloveVarianty.map(
            (variant) => {
              const textCiela =
                textDosiahnutiaCiela(
                  variant
                );

              return (
                <div
                  key={variant.key}
                  className="invest-graf-realne-row"
                >
                  <span>
                    {variant.label}
                  </span>

                  <span>
                    {textCiela.text}
                    {textCiela.zvyraznenie && (
                      <strong>
                        {" "}
                        {textCiela.zvyraznenie}
                      </strong>
                    )}
                  </span>
                </div>
              );
            }
          )}
        </div>
      )}
    </>
  );
}
  );

export default
  InvesticneVyvojGrafKarta;